import { useEffect, useState } from 'react';
import {
  currentMode,
  getPreference,
  setPreference as persistPreference,
  subscribe,
  type ThemeMode,
  type ThemePreference,
} from '../lib/theme';
import { paletteFor, type BrandPalette } from '../lib/brand';

interface UseThemeResult {
  /** The mode currently painted. */
  mode: ThemeMode;
  /** What the visitor asked for. `auto` means "follow the clock". */
  preference: ThemePreference;
  /** Brand colours for the current mode, for props that cannot take a class. */
  palette: BrandPalette;
  setPreference: (preference: ThemePreference) => void;
  /** Flip to the opposite of what is showing, and remember it. */
  toggle: () => void;
}

/**
 * Read and steer the site's light/dark mode.
 *
 * The mode itself is owned by src/lib/theme.ts (clock-driven); this hook just
 * subscribes so React re-renders when it flips — on the hour, on tab refocus, or
 * when the visitor uses the header control.
 */
export function useTheme(): UseThemeResult {
  const [mode, setMode] = useState<ThemeMode>(() => currentMode());
  const [preference, setPreferenceState] = useState<ThemePreference>(() => getPreference());

  useEffect(() => {
    setMode(currentMode());
    return subscribe(setMode);
  }, []);

  const setPreference = (next: ThemePreference) => {
    const applied = persistPreference(next);
    setPreferenceState(next);
    setMode(applied);
  };

  return {
    mode,
    preference,
    palette: paletteFor(mode),
    setPreference,
    toggle: () => setPreference(mode === 'dark' ? 'light' : 'dark'),
  };
}
